import { type CreateDepartmentInput, type Department, createDepartmentSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { FormActions } from '#client/components/ui/FormActions';
import { NumberField } from '#client/components/ui/NumberField';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';

const PATH = RESOURCE.departments;

export function DepartmentFormPage() {
  return (
    <ResourceFormShell<Department> path={PATH} noun="department">
      {({ id, record }) => <DepartmentForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function DepartmentForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: Department | undefined;
}) {
  const { values, set } = useFormValues<CreateDepartmentInput>({
    name: record?.name ?? '',
    code: record?.code ?? '',
    // New benches sort to the end rather than to the front, which is almost
    // always what someone adding one means.
    sort_order: record?.sort_order ?? 100,
    is_active: record?.is_active ?? true,
  });

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createDepartmentSchema,
    noun: 'Department',
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card>
          <div className="c-form-grid">
            <TextField
              label="Name"
              value={values.name}
              error={form.errors.name}
              onChange={(event) => set('name', event.target.value)}
              helpText="Printed as the rate list's group heading"
              required
            />
            <TextField
              label="Code"
              value={values.code}
              error={form.errors.code}
              onChange={(event) => set('code', event.target.value)}
              helpText="Short and unique, e.g. SER. Imports match on this."
              required
            />
            <NumberField
              label="Sort order"
              min={0}
              value={values.sort_order}
              error={form.errors.sort_order}
              onChange={(value) => set('sort_order', value)}
              helpText="Lower numbers print first. Ties break alphabetically."
            />
          </div>

          <Checkbox
            label="Active"
            checked={values.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
          />
        </Card>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Create department'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
